import React, {
  Dispatch,
  memo,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import useFormWithSchema from "../../hooks/useFormWithSchema";
import * as Yup from "yup";
import { Controller } from "react-hook-form";
import ComboboxInput from "../inputs/ComboboxInput";
import Accordian from "../accordian";
import { useTranslation } from "react-i18next";
import {
  useGetAllBuildingsById,
  useGetAllLocationsDropdown,
  useGetAllSystemDetailCodesDropdown,
} from "../../hooks/common/query";
import { mapToOptions } from "../../lib/functions";
import { SYSTEM_DETAIL_CODES } from "../../constants/common";
import { Button } from "../button";
import { useGetAllEvacuationEvents } from "../../hooks/emergency-evacuation/query";

interface SearchEvacuationEventsCardProps {
  setAllEvacuationEvents: Dispatch<SetStateAction<never[]>>;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  page: number;
  setPages: Dispatch<SetStateAction<number>>;
  search: string;
  setCount: Dispatch<SetStateAction<number>>;
  pageSize: number;
  setPage: Dispatch<SetStateAction<number>>;
}

const SearchEvacuationEventsCardSchema = Yup.object({
  location: Yup.string(),
  building: Yup.string(),
  eventType: Yup.string(),
  participantType: Yup.string(),
  status: Yup.string(),
});

const SearchEvacuationEventsCard = ({
  setAllEvacuationEvents,
  setIsLoading,
  page,
  setPages,
  search,
  setCount,
  pageSize,
  setPage,
}: SearchEvacuationEventsCardProps) => {
  const {
    control,
    formState: { errors },
    watch,
    resetField,
    setValue,
  } = useFormWithSchema(SearchEvacuationEventsCardSchema);

  const location = watch("location");
  const building = watch("building");
  const eventType = watch("eventType");
  const participantType = watch("participantType");
  const status = watch("status");

  const { t } = useTranslation();

  const [selectedKey, setSelectedKey] = useState<string>("");

  const { data: allLocations } = useGetAllLocationsDropdown();
  const { data: allBuildings } = useGetAllBuildingsById({
    encryptedId: location ? location.toString() : "",
  });

  const { data: allEventTypes } = useGetAllSystemDetailCodesDropdown({
    encryptedId: SYSTEM_DETAIL_CODES.MUSTER_EVENT_TYPE,
  });
  // const { data: allWhoToIncludes } = useGetAllSystemDetailCodesDropdown({
  //   encryptedId: SYSTEM_DETAIL_CODES.EMERGENCY_EVACUATION_PERSON_TYPE,
  // });
  const { data: allEventStatuses } = useGetAllSystemDetailCodesDropdown({
    encryptedId: SYSTEM_DETAIL_CODES.EMERGENCY_EVACUATION_STATUS,
  });

  const buildings = useMemo(
    () =>
      allBuildings
        ? mapToOptions(
            "s_BuildingName_En",
            "s_BuildingName_Ar",
            "n_BuildingID",
            allBuildings,
          )
        : [],
    [allBuildings],
  );

  const { data: evacuationEvents, isPending: isEvacuationEventsLoading } =
    useGetAllEvacuationEvents({
      pageNumber: page,
      pageSize: pageSize,
      search: search,
      n_LocationID: location ? Number(location) : undefined,
      n_BuildingID: building ? Number(building) : undefined,
      s_EventTypeCode: eventType,
      s_EventPersonTypeCode: participantType,
      s_StatusCode: status,
    });

  useEffect(() => {
    setIsLoading?.(isEvacuationEventsLoading);
  }, [isEvacuationEventsLoading, setIsLoading]);

  useEffect(() => {
    if (evacuationEvents) {
      setAllEvacuationEvents(evacuationEvents.data || []);
      setPages(evacuationEvents.pagination?.pages || 1);
      setCount(evacuationEvents.pagination?.count || 0);
    }
  }, [setCount, setAllEvacuationEvents, setPages, evacuationEvents]);

  useEffect(() => {
    setPage(1);
  }, [setPage, location, building, eventType, participantType, status]);

  const locations = useMemo(
    () =>
      mapToOptions(
        "s_LocationName_En",
        "s_LocationName_Ar",
        "n_LocationID",
        allLocations,
      ),
    [allLocations],
  );

  const eventTypes = useMemo(
    () => mapToOptions("s_Desc_E", "s_Desc_A", "n_DetailedCode", allEventTypes),
    [allEventTypes],
  );

  // const whoToIncludes = useMemo(
  //   () =>
  //     mapToOptions("s_Desc_E", "s_Desc_A", "n_DetailedCode", allWhoToIncludes),
  //   [allWhoToIncludes],
  // );

  const eventStatuses = useMemo(
    () =>
      mapToOptions("s_Desc_E", "s_Desc_A", "n_DetailedCode", allEventStatuses),
    [allEventStatuses],
  );

  const onClear = useCallback(() => {
    resetField("location", undefined);
    resetField("building", undefined);
    resetField("eventType", undefined);
    resetField("participantType", undefined);
    resetField("status", undefined);
  }, [resetField]);

  return (
    <div className="bg-white p-2 md:p-4 rounded-xl flex flex-col gap-3 lg:gap-6 shadow-[0px_1px_5px_0px_rgba(113,112,163,0.3)]">
      <Accordian
        id="1"
        selectedKey={selectedKey}
        onSelectionChange={setSelectedKey}
        title={
          <h1 className="text-xl md:text-2xl flex flex-1 -mt-2">
            {t("searchFilters")}
          </h1>
        }
      >
        <div className="flex flex-col gap-4">
          <form className="flex flex-col gap-4">
            <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 lg:gap-4">
              <Controller
                name="location"
                control={control}
                render={({ field: { onChange, name, value } }) => (
                  <ComboboxInput
                    options={locations}
                    value={
                      locations?.find((item) => item.value === Number(value))
                        ?.labelEn || ""
                    }
                    onChange={(value) => {
                      onChange(value);
                      setValue("building", "");
                    }}
                    error={errors?.[name]?.message}
                    label={t("location")}
                  />
                )}
              />

              <Controller
                name="building"
                control={control}
                render={({ field: { onChange, name, value } }) => (
                  <ComboboxInput
                    options={buildings}
                    value={
                      buildings?.find((item) => item.value === Number(value))
                        ?.labelEn || ""
                    }
                    onChange={onChange}
                    error={errors?.[name]?.message}
                    label={t("building")}
                  />
                )}
              />

              <Controller
                name="eventType"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <ComboboxInput
                    options={eventTypes}
                    onChange={onChange}
                    label={t("eventType")}
                    value={
                      eventTypes?.find((item) => item.value === value)
                        ?.labelEn || ""
                    }
                  />
                )}
              />

              {/* <Controller
                name="participantType"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <ComboboxInput
                    options={whoToIncludes}
                    onChange={onChange}
                    label={t("participantType")}
                    value={
                      whoToIncludes?.find((item) => item.value === value)
                        ?.labelEn || ""
                    }
                  />
                )}
              /> */}

              <Controller
                name="status"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <ComboboxInput
                    options={eventStatuses}
                    onChange={onChange}
                    label={t("status")}
                    value={
                      eventStatuses?.find((item) => item.value === value)
                        ?.labelEn || ""
                    }
                  />
                )}
              />
            </div>
          </form>

          <div className="flex items-center gap-2">
            <Button
              type="button"
              onClick={onClear}
              className="bg-transparent ring-1 ring-purple-primary text-purple-primary hover:bg-transparent"
            >
              {t("clear")}
            </Button>
          </div>
        </div>
      </Accordian>
    </div>
  );
};

export default memo(SearchEvacuationEventsCard);
